import React, { useEffect, useState } from 'react';

export type TriggerPreset = 'hourly' | 'daily' | 'weekdays' | 'weekly' | 'monthly' | 'custom';

interface TriggerState {
  preset: TriggerPreset;
  hour: number;
  minute: number;
  dayOfWeek: number;
  dayOfMonth: number;
}

const PRESET_LABELS: { value: TriggerPreset; label: string; description: string }[] = [
  { value: 'hourly', label: 'Cada hora', description: 'Se ejecuta una vez por hora' },
  { value: 'daily', label: 'Diario', description: 'Una vez al día a la hora que elijas' },
  { value: 'weekdays', label: 'Días hábiles', description: 'Lunes a viernes a la hora que elijas' },
  { value: 'weekly', label: 'Semanal', description: 'Una vez a la semana el día que elijas' },
  { value: 'monthly', label: 'Mensual', description: 'Una vez al mes el día que elijas' },
  { value: 'custom', label: 'Avanzado', description: 'Expresión cron avanzada' },
];

const DAYS_OF_WEEK = [
  { value: 1, label: 'Lunes' },
  { value: 2, label: 'Martes' },
  { value: 3, label: 'Miércoles' },
  { value: 4, label: 'Jueves' },
  { value: 5, label: 'Viernes' },
  { value: 6, label: 'Sábado' },
  { value: 0, label: 'Domingo' },
];

const DAYS_OF_MONTH = Array.from({ length: 28 }, (_, i) => ({ value: i + 1, label: `${i + 1}` }));

const HOURS = Array.from({ length: 24 }, (_, i) => ({
  value: i,
  label: i.toString().padStart(2, '0'),
}));

const MINUTES = [0, 5, 10, 15, 20, 30, 45].map((m) => ({
  value: m,
  label: m.toString().padStart(2, '0'),
}));

function buildCron(state: TriggerState): string {
  const { preset, hour, minute, dayOfWeek, dayOfMonth } = state;
  switch (preset) {
    case 'hourly':
      return `${minute} * * * *`;
    case 'daily':
      return `${minute} ${hour} * * *`;
    case 'weekdays':
      return `${minute} ${hour} * * 1-5`;
    case 'weekly':
      return `${minute} ${hour} * * ${dayOfWeek}`;
    case 'monthly':
      return `${minute} ${hour} ${dayOfMonth} * *`;
    case 'custom':
      return '';
  }
}

function detectPreset(cron: string): TriggerState | null {
  const parts = cron.trim().split(/\s+/);
  if (parts.length !== 5) return null;
  const [min, hr, dom, , dow] = parts;

  const minute = parseInt(min, 10);
  const hour = parseInt(hr, 10);

  if (hr === '*' && dom === '*' && dow === '*') {
    return { preset: 'hourly', hour: 9, minute: isNaN(minute) ? 0 : minute, dayOfWeek: 1, dayOfMonth: 1 };
  }
  if (dom === '*' && dow === '1-5') {
    return { preset: 'weekdays', hour: isNaN(hour) ? 9 : hour, minute: isNaN(minute) ? 0 : minute, dayOfWeek: 1, dayOfMonth: 1 };
  }
  if (dom === '*' && /^\d$/.test(dow)) {
    return { preset: 'weekly', hour: isNaN(hour) ? 9 : hour, minute: isNaN(minute) ? 0 : minute, dayOfWeek: parseInt(dow, 10), dayOfMonth: 1 };
  }
  if (/^\d+$/.test(dom) && dow === '*') {
    return { preset: 'monthly', hour: isNaN(hour) ? 9 : hour, minute: isNaN(minute) ? 0 : minute, dayOfWeek: 1, dayOfMonth: parseInt(dom, 10) };
  }
  if (dom === '*' && dow === '*') {
    return { preset: 'daily', hour: isNaN(hour) ? 9 : hour, minute: isNaN(minute) ? 0 : minute, dayOfWeek: 1, dayOfMonth: 1 };
  }
  return null;
}

interface CronBuilderProps {
  value: string;
  onChange: (cron: string) => void;
  cronValidation?: { valid: boolean; errors?: string[]; nextRuns?: string[] } | null;
  onValidate?: () => void;
  validating?: boolean;
}

export const CronBuilder: React.FC<CronBuilderProps> = ({ value, onChange, cronValidation, onValidate, validating }) => {
  const detected = detectPreset(value);

  const [state, setState] = useState<TriggerState>(
    detected ?? { preset: value ? 'custom' : 'monthly', hour: 9, minute: 0, dayOfWeek: 1, dayOfMonth: 1 }
  );
  const [customCron, setCustomCron] = useState(detected ? '' : value);

  useEffect(() => {
    if (state.preset !== 'custom') {
      const cron = buildCron(state);
      if (cron !== value) onChange(cron);
    }
  }, [state]);

  const updateState = (patch: Partial<TriggerState>) => {
    setState((prev) => ({ ...prev, ...patch }));
  };

  const selectPreset = (preset: TriggerPreset) => {
    if (preset === 'custom') {
      setState((prev) => ({ ...prev, preset: 'custom' }));
      setCustomCron(value);
    } else {
      setState((prev) => ({ ...prev, preset }));
    }
  };

  const handleCustomChange = (val: string) => {
    setCustomCron(val);
    onChange(val);
  };

  const inputClass =
    'px-3 py-1.5 rounded-lg border border-slate-200 text-sm text-slate-700 bg-white focus:outline-none focus:ring-2 focus:ring-sky-400/50';

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-3 gap-2 sm:grid-cols-6">
        {PRESET_LABELS.map((p) => (
          <button
            key={p.value}
            type="button"
            onClick={() => selectPreset(p.value)}
            title={p.description}
            className={`px-2 py-2 rounded-lg border text-xs font-medium transition-colors ${
              state.preset === p.value
                ? 'bg-sky-500 text-white border-sky-500'
                : 'bg-white text-slate-600 border-slate-200 hover:border-sky-300 hover:text-sky-600'
            }`}
          >
            {p.label}
          </button>
        ))}
      </div>

      {state.preset === 'hourly' && (
        <div className="flex items-center gap-3 flex-wrap">
          <span className="text-sm text-slate-600">En el minuto</span>
          <select
            value={state.minute}
            onChange={(e) => updateState({ minute: parseInt(e.target.value, 10) })}
            className={inputClass}
          >
            {MINUTES.map((m) => (
              <option key={m.value} value={m.value}>{m.label}</option>
            ))}
          </select>
          <span className="text-xs text-slate-400">de cada hora</span>
        </div>
      )}

      {(state.preset === 'daily' || state.preset === 'weekdays') && (
        <div className="flex items-center gap-3 flex-wrap">
          <span className="text-sm text-slate-600">A las</span>
          <select
            value={state.hour}
            onChange={(e) => updateState({ hour: parseInt(e.target.value, 10) })}
            className={inputClass}
          >
            {HOURS.map((h) => (
              <option key={h.value} value={h.value}>{h.label}</option>
            ))}
          </select>
          <span className="text-sm text-slate-500">:</span>
          <select
            value={state.minute}
            onChange={(e) => updateState({ minute: parseInt(e.target.value, 10) })}
            className={inputClass}
          >
            {MINUTES.map((m) => (
              <option key={m.value} value={m.value}>{m.label}</option>
            ))}
          </select>
          {state.preset === 'weekdays' && (
            <span className="text-xs text-slate-400">de lunes a viernes</span>
          )}
        </div>
      )}

      {state.preset === 'weekly' && (
        <div className="flex items-center gap-3 flex-wrap">
          <span className="text-sm text-slate-600">Cada</span>
          <select
            value={state.dayOfWeek}
            onChange={(e) => updateState({ dayOfWeek: parseInt(e.target.value, 10) })}
            className={inputClass}
          >
            {DAYS_OF_WEEK.map((d) => (
              <option key={d.value} value={d.value}>{d.label}</option>
            ))}
          </select>
          <span className="text-sm text-slate-600">a las</span>
          <select
            value={state.hour}
            onChange={(e) => updateState({ hour: parseInt(e.target.value, 10) })}
            className={inputClass}
          >
            {HOURS.map((h) => (
              <option key={h.value} value={h.value}>{h.label}</option>
            ))}
          </select>
          <span className="text-sm text-slate-500">:</span>
          <select
            value={state.minute}
            onChange={(e) => updateState({ minute: parseInt(e.target.value, 10) })}
            className={inputClass}
          >
            {MINUTES.map((m) => (
              <option key={m.value} value={m.value}>{m.label}</option>
            ))}
          </select>
        </div>
      )}

      {state.preset === 'monthly' && (
        <div className="flex items-center gap-3 flex-wrap">
          <span className="text-sm text-slate-600">El día</span>
          <select
            value={state.dayOfMonth}
            onChange={(e) => updateState({ dayOfMonth: parseInt(e.target.value, 10) })}
            className={inputClass}
          >
            {DAYS_OF_MONTH.map((d) => (
              <option key={d.value} value={d.value}>{d.label}</option>
            ))}
          </select>
          <span className="text-sm text-slate-600">de cada mes, a las</span>
          <select
            value={state.hour}
            onChange={(e) => updateState({ hour: parseInt(e.target.value, 10) })}
            className={inputClass}
          >
            {HOURS.map((h) => (
              <option key={h.value} value={h.value}>{h.label}</option>
            ))}
          </select>
          <span className="text-sm text-slate-500">:</span>
          <select
            value={state.minute}
            onChange={(e) => updateState({ minute: parseInt(e.target.value, 10) })}
            className={inputClass}
          >
            {MINUTES.map((m) => (
              <option key={m.value} value={m.value}>{m.label}</option>
            ))}
          </select>
        </div>
      )}

      {state.preset === 'custom' && (
        <div className="space-y-2">
          <div className="flex gap-2">
            <input
              type="text"
              value={customCron}
              onChange={(e) => handleCustomChange(e.target.value)}
              placeholder="0 9 1 * *"
              className={`${inputClass} flex-1 font-mono`}
            />
            {onValidate && (
              <button
                type="button"
                onClick={onValidate}
                disabled={validating}
                className="px-3 py-1.5 rounded-lg border border-slate-200 text-sm text-slate-600 hover:bg-slate-50 disabled:opacity-50"
              >
                {validating ? 'Validando...' : 'Validar'}
              </button>
            )}
          </div>
          <p className="text-xs text-slate-400">
            Formato: minuto hora día-mes mes día-semana · Ejemplo: <code className="font-mono">0 9 1 * *</code> = el 1ro de cada mes a las 09:00
          </p>
        </div>
      )}

      {cronValidation && (
        <div className={`text-xs rounded-lg px-3 py-2 ${cronValidation.valid ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-600'}`}>
          {cronValidation.valid ? (
            <>
              <p className="font-medium">Expresión válida</p>
              {cronValidation.nextRuns && cronValidation.nextRuns.length > 0 && (
                <p className="mt-0.5 text-emerald-600">
                  Próximas ejecuciones: {cronValidation.nextRuns.slice(0, 3).join(' · ')}
                </p>
              )}
            </>
          ) : (
            <>
              <p className="font-medium">Expresión inválida</p>
              {cronValidation.errors?.map((e, i) => <p key={i}>{e}</p>)}
            </>
          )}
        </div>
      )}

      {state.preset !== 'custom' && value && (
        <div className="flex items-center gap-1.5 text-xs text-slate-500 pt-1">
          <span>Cron generado</span>
          <code className="px-2 py-0.5 rounded-md bg-slate-100 text-slate-700 font-mono text-xs border border-slate-200">
            {value}
          </code>
        </div>
      )}
    </div>
  );
};
